import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useState, type FC } from "react";
import toast from "react-hot-toast";
import { useFlags } from "../../../../server/optimizely";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../utils/uuid";
import type { ChannelName } from "../../../channels/types";
import { $isDynamicContentNode } from "../../../message/editor/nodes/dynamic-content/dynamic-content-node";
import { $isDynamicContentToolbarNode } from "../../../message/editor/nodes/dynamic-content/helpers";
import { $isPollNode } from "../../../message/editor/nodes/poll/helpers";
import type { SerialisedLexicalEditorDesign } from "../../../message/editor/types/serialised-editor-design";
import { convertLexicalDesign } from "../../../moment/component/channel/utils/convert-lexical-design";

export const FollowUpMessageImport: FC<{
  id: UUID | null;
  channel: ChannelName;
  setDesign: (design: SerialisedLexicalEditorDesign) => void;
}> = ({ id, channel, setDesign }) => {
  const dialog = useDialog();
  const [loading, setLoading] = useState(false);
  const {
    flags: { cha_4024_import_follow_up_content },
  } = useFlags("cha_4024_import_follow_up_content");

  const { handleClose } = dialog;

  const handleImport = useSupabaseCallback(
    async ({ supabase }) => {
      if (!id || !channel) return;

      setLoading(true);

      const { data, error } = await supabase
        .from("moment")
        .select("design,channel")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error importing message:", error);
        toast.error("Unable to import message");
        setLoading(false);
        return;
      }

      if (!data.channel) {
        console.error("Parent message has no channel selected");
        toast.error("Parent message has no channel selected");
        setLoading(false);
        handleClose();
        return;
      }

      if (!data.design) {
        console.error("Parent message has no design");
        toast.error("Parent message has no design");
        setLoading(false);
        handleClose();
        return;
      }

      setDesign(
        convertLexicalDesign(
          channel,
          data.channel,
          data.design as SerialisedLexicalEditorDesign,
          false,
          [],
          [$isDynamicContentNode, $isDynamicContentToolbarNode, $isPollNode],
        ),
      );

      setLoading(false);
      toast.success("Content imported successfully");
      handleClose();
    },
    [id, channel, handleClose, setDesign],
  );

  if (!cha_4024_import_follow_up_content) return null;

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={dialog.handleOpen}
        data-analytics-id="follow-up-message-import"
        disabled={!id || !channel}
        loading={loading}
      >
        Import content from original Communication
      </Button>
      <Dialog open={dialog.open} onClose={dialog.handleClose}>
        <DialogTitle>Import content from original Communication</DialogTitle>
        <DialogContent>
          <p>This will replace any content you have added to the Follow-Up already.</p>
          <p>Remember to edit the text so it acts as a Follow-Up to the original Communication!</p>
          <p>
            Any dynamic content in the original Communication will be removed. If you are using a
            different channel for the Follow-Up, we will try our best to import the content, however
            some formatting may be lost.
          </p>
          <p>
            Bear in mind, if the content of the original Communication changes, it won’t be updated
            in this Follow-Up.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={dialog.handleClose}
            color="primary"
            variant="text"
            data-analytics-id="follow-up-message-import-cancel"
            loading={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleImport()}
            color="primary"
            variant="contained"
            data-analytics-id="follow-up-message-import-import"
            loading={loading}
          >
            Import content
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
